import React, { useState, useEffect } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';
import CustomPollCard from '../components/CustomPollCard';
import CreateCustomPollModal from '../components/CreateCustomPollModal';
import { MdAdd, MdInfo } from 'react-icons/md';

function MyPollsPage() {
  const [polls, setPolls] = useState([]);
  const [pollPoints, setPollPoints] = useState(0);
  const [pollPointsBalance, setPollPointsBalance] = useState(0);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pollsRes, tagsRes, achievementsRes] = await Promise.all([
        axios.get(`${API_URL}/api/custom-polls/my-polls`),
        axios.get(`${API_URL}/api/tags`),
        axios.get(`${API_URL}/api/user/achievements`)
      ]);
      
      setPolls(pollsRes.data.polls);
      setPollPoints(achievementsRes.data?.pollPoints?.total ?? pollsRes.data.pollPoints);
      setPollPointsBalance(pollsRes.data.pollPointsBalance ?? pollsRes.data.pollPoints);
      setTags(tagsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePollCreated = (newPoll) => {
    // Reload all data to get fresh poll points and updated poll list from server
    loadData();
    setShowCreateModal(false);
  };

  const filteredPolls = activeFilter === 'all' 
    ? polls 
    : polls.filter(p => p.status === activeFilter);

  const getStatusCounts = () => {
    return {
      all: polls.length,
      pending: polls.filter(p => p.status === 'pending').length,
      active: polls.filter(p => p.status === 'active').length,
      closed: polls.filter(p => p.status === 'closed').length,
      rejected: polls.filter(p => p.status === 'rejected').length,
      flagged: polls.filter(p => p.status === 'flagged').length
    };
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Poll Points & Create Button */}
        <div className="card-container mb-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div>
                <div className="text-sm font-medium text-gray-500 mb-1 font-rubik">Your Poll Points</div>
                <div className="text-4xl font-bold text-tertiary font-rubik" data-testid="my-poll-points">{pollPoints}</div>
                <div className="text-xs text-gray-500 font-rubik mt-1">Available to spend: <span className="font-semibold text-tertiary">{pollPointsBalance}</span></div>
              </div>
              <div className="h-16 w-px bg-gray-200 hidden md:block"></div>
              <div className="text-sm text-gray-600 font-rubik">
                <p className="font-medium mb-1">Poll Duration Costs:</p>
                <p>• 1 Day = 20 points</p>
                <p>• 7 Days = 100 points</p>
                <p>• 30 Days = 500 points</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2 px-6 py-3"
              data-testid="create-poll-button"
            >
              <MdAdd className="text-xl" />
              Create Custom Poll
            </button>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="card-container mb-6">
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All', color: 'bg-gray-500' },
              { key: 'pending', label: 'Pending', color: 'bg-yellow-500' },
              { key: 'active', label: 'Active', color: 'bg-green-500' },
              { key: 'closed', label: 'Closed', color: 'bg-gray-600' },
              { key: 'flagged', label: 'Flagged', color: 'bg-orange-500' },
              { key: 'rejected', label: 'Rejected', color: 'bg-red-500' }
            ].map(({ key, label, color }) => (
              <button
                key={key}
                onClick={() => setActiveFilter(key)}
                className={`px-4 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
                  activeFilter === key
                    ? `${color} text-white`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`filter-${key}`}
              >
                {label} ({statusCounts[key]})
              </button>
            ))}
          </div>
        </div>

        {/* Polls List */}
        <div className="space-y-4">
          {filteredPolls.length === 0 ? (
            <div className="card-container text-center py-12">
              <MdInfo className="text-5xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2 font-rubik">
                {activeFilter === 'all' 
                  ? "You haven't created any custom polls yet" 
                  : `No ${activeFilter} polls found`}
              </h3>
              {activeFilter === 'all' && (
                <p className="text-gray-500 font-rubik">Click &quot;Create Custom Poll&quot; to get started!</p>
              )}
            </div>
          ) : (
            filteredPolls.map(poll => (
              <CustomPollCard 
                key={poll.customPollId} 
                poll={poll} 
                tags={tags}
                isOwnerView={true}
                onPollUpdate={loadData}
              />
            ))
          )}
        </div>

      {/* Create Poll Modal */}
      {showCreateModal && (
        <CreateCustomPollModal
          tags={tags}
          pollPoints={pollPointsBalance}
          onClose={() => setShowCreateModal(false)}
          onPollCreated={handlePollCreated}
        />
      )}
    </div>
  );
}

export default MyPollsPage;
